import React, { useEffect, useRef, useState } from 'react';
import { Icon } from "@iconify/react/dist/iconify.js";
import DailySummary from "./child/DailySummary";
import { Link } from "react-router-dom";
import { useOnboardingStatus, useUserProfile } from "../masterLayout/MasterLayoutBody";
import getLogoSrc from "../utils/logoUtils";
import { getOrganisationById, updateOrganisation } from '../api/organisationAPI';
import { getHierarchyStructures } from '../api/hierarchy-structureAPI';
import QRCode from 'qrcode';
import InviteUsersModal from "./InviteUsersModal";
import { triggerWorkOrderRecalculation } from '../api/work-order-recalculationAPI';
import { useAppSettings, useSetting } from '../utils/AppSettingsContext';
import { useTourGuide } from '../utils/useTourGuide';

const HOME_TOUR_OPTIONS = {
  debug: false,
  keyboardControls: true,
  exitOnClickOutside: true,
  exitOnEscape: true,
  completeOnFinish: false,
  resumeOnLoad: false,
  rememberStep: false,
  dialogZ: 20000,
  dialogMaxWidth: 560,
  dialogClass: 'tourguide-step-card',
};
const PRODUCT_TOUR_TRIGGER_KEY = 'samurai:product-tour:trigger';
const PRODUCT_TOUR_TRIGGER_ONCE_KEY = 'samurai:product-tour:trigger-once';
const HOME_SKIP_AUTO_TOUR_ONCE_KEY = 'samurai:home:skip-auto-tour-once';

const HOME_ACTION_START_ORDER = 8;

const HomeLayer = () => {
  const userProfile = useUserProfile();
  const { loaded: onboardingLoaded, showSetupPrompt, hasWorkOrderRecords } = useOnboardingStatus();
  const appSettingsContext = useAppSettings() || {};
  const { appSettings } = appSettingsContext;
  const tourGuideEnabled = useSetting('tour_guide_enabled', true);
  const tourGuideSettingLoaded = Object.prototype.hasOwnProperty.call(appSettings || {}, 'tour_guide_enabled');
  const canUseTourGuide = tourGuideSettingLoaded ? tourGuideEnabled : true;
  const { ready: tourGuideReady, startTour } = useTourGuide({
    group: 'home-tour',
    enabled: canUseTourGuide,
    options: HOME_TOUR_OPTIONS,
    minSteps: 3,
  });
  const autoTourTimerRef = useRef(null);
  const autoTourStartedRef = useRef(false);
  const lastManualTourTriggerRef = useRef('');
  const userFirstName = userProfile?.name?.split(" ")[0] ?? "";
  const [orgLogo, setOrgLogo] = useState('');
  const [qrPlayImageUrl, setQrPlayImageUrl] = useState(null);
  const [qrIosImageUrl, setQrIosImageUrl] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [pageAccess, setPageAccess] = useState([]);
  const [triggering, setTriggering] = useState(false);
  const [triggerMessage, setTriggerMessage] = useState('');
  const [triggerError, setTriggerError] = useState('');
  const [hasDepartmentRecords, setHasDepartmentRecords] = useState(true);
  const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
  const [logoDraft, setLogoDraft] = useState('');
  const [isSavingLogo, setIsSavingLogo] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [organisationRecord, setOrganisationRecord] = useState(null);
  const [isLogoHovered, setIsLogoHovered] = useState(false);
  const maxLogoSize = 5 * 1024 * 1024;
  const samuraiAppLink = 'https://play.google.com/store/apps/details?id=com.samurai.cmms&hl=en;';
  const samuraiIosLink = 'https://apps.apple.com/app/samurai-cmms/id6753664059 ';

  // Check is user has access to pages and if not, hide the link
  const pageChecks = {
    showEvents: '/events',
    showWorkOrders: '/work-orders',
    showWorkRequests: '/notifications',
    showForms: '/forms',
    showMeters: '/meter-readings',
    showAreas: '/area',
    showAssets: '/asset-list',
    showInventory: '/stock-items',
    showDataLoading: '/data-loading',
  };

  useEffect(() => {
    const access = Object.fromEntries(
      Object.entries(pageChecks).map(([key, path]) => [key, userProfile?.pages.includes(path)])
    );
    setPageAccess(access);
  }, [userProfile]);

  useEffect(() => {
    const qrOptions = {
      width: 100,
      color: {
        dark: '#0d6efd',
        light: '#ffffff'
      }
    };
    QRCode.toDataURL(samuraiAppLink, qrOptions)
      .then(setQrPlayImageUrl)
      .catch(err => console.error('QR code generation failed', err));
  }, []);

  useEffect(() => {
    const qrOptions = {
      width: 100,
      color: {
        dark: '#0d6efd',
        light: '#ffffff'
      }
    };
    QRCode.toDataURL(samuraiIosLink, qrOptions)
      .then(setQrIosImageUrl)
      .catch(err => console.error('QR code generation failed', err));
  }, []);

  useEffect(() => {
    const fetchOrganisation = async () => {
      if (userProfile?.org_id) {
        try {
          const org = await getOrganisationById(userProfile.org_id);
          setOrganisationRecord(org);
          setOrgLogo(org.orgLogo);
        } catch (err) {
          console.error('Error fetching organisation', err);
        }
      }
    };
    fetchOrganisation();
  }, [userProfile?.org_id]);

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const response = await getHierarchyStructures();
        const records = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];
        setHasDepartmentRecords(records.length > 0);
      } catch (error) {
        console.error('Error loading hierarchy structures', error);
        setHasDepartmentRecords(true);
      }
    };

    loadDepartments();
  }, []);

  const cards = [
    {
      title: "Manage Events",
      icon: "mdi:calendar-month-outline",
      description: "Create and manage downtime events",
      link: "/events",
      action: "View Events",
      visible: pageAccess.showEvents,
    },
    {
      title: "Manage Work Orders",
      icon: "mage:checklist-note",
      description: "Create and assign new work",
      link: "/work-orders",
      action: "View Work Orders",
      visible: pageAccess.showWorkOrders,
    },
    {
      title: "Add Work Requests",
      icon: "mage:archive",
      description: "Submit a work request",
      link: "/notifications",
      action: "View Work Requests",
      visible: pageAccess.showWorkRequests,
    },
    {
      title: "Create a New Form",
      icon: "mage:clipboard",
      description: "Design a custom form",
      link: "/forms",
      action: "Create Form",
      visible: pageAccess.showForms,
    },

    {
      title: "Enter Meter Readings",
      icon: "mage:health-circle",
      description: "Record latest usage data",
      link: "/meter-readings",
      action: "Enter Readings",
      visible: pageAccess.showMeters,
    },
    {
      title: "Manage Your Assets",
      icon: "mage:delivery-truck",
      description: "Register new equipment",
      link: "/asset-list",
      action: "View Assets",
      visible: pageAccess.showAssets,
    },
    /*{
      title: "Add Areas",
      icon: "mage:map-marker",
      description: "Define new site areas",
      link: "/area",
      action: "View Areas",
      visible: pageAccess.showAreas,
    },*/
    {
      title: "Manage Inventory",
      icon: "mage:shopping-cart",
      description: "Manage inventory & stock",
      link: "/stock-items",
      action: "Manage Inventory",
      visible: pageAccess.showInventory,
    },
    {
      title: "Bulk Load Data",
      icon: "mage:archive",
      description: "Import data in bulk",
      link: "/data-loading",
      action: "Load Data",
      visible: pageAccess.showDataLoading,
    },
  ];

  const logoSrc = getLogoSrc(orgLogo) || 'assets/images/org.png';
  const tenantId = userProfile?.tenantId ?? null;
  const normalizedRoleNames =
    userProfile?.roles?.map((role) =>
      typeof role?.name === 'string' ? role.name.toLowerCase() : undefined,
    ) ?? [];
  const isAdminOrSuperAdmin = normalizedRoleNames.includes('admin') || normalizedRoleNames.includes('superadmin');
  const canInvite = tenantId !== null && tenantId !== undefined;
  const canSeeInviteCard = isAdminOrSuperAdmin;

  const isSuperAdmin = userProfile?.roles?.some((role) => role?.id === 1);

  const handleRecalculation = async () => {
    setTriggering(true);
    setTriggerMessage('');
    setTriggerError('');
    try {
      await triggerWorkOrderRecalculation();
      setTriggerMessage('Work order recalculation has been triggered successfully.');
    } catch (err) {
      console.error('Failed to trigger work order recalculation', err);
      setTriggerError('Failed to trigger recalculation. Please try again.');
    } finally {
      setTriggering(false);
    }
  };

  const handleStartTour = async () => {
    if (!canUseTourGuide) {
      return;
    }

    try {
      if (autoTourTimerRef.current) {
        window.clearTimeout(autoTourTimerRef.current);
        autoTourTimerRef.current = null;
      }
      await startTour();
    } catch (err) {
      console.error('Failed to start home tour', err);
    }
  };

  const openLogoModal = () => {
    setLogoDraft(orgLogo || '');
    setLogoError('');
    setIsLogoModalOpen(true);
  };

  const handleLogoFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > maxLogoSize) {
      setLogoError('File is too large. Maximum allowed size is 5MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      const base64 = loadEvent.target?.result;
      if (typeof base64 === 'string') {
        setLogoDraft(base64);
        setLogoError('');
      }
    };
    reader.onerror = () => {
      setLogoError('Unable to read selected image.');
    };
    reader.readAsDataURL(file);
  };

  const handleSaveLogo = async () => {
    const orgId = userProfile?.org_id;
    if (!orgId || !logoDraft) {
      setLogoError('Please select a logo image first.');
      return;
    }

    setIsSavingLogo(true);
    setLogoError('');
    try {
      const payload = {
        ...(organisationRecord || {}),
        orgLogo: logoDraft,
      };
      const updated = await updateOrganisation(orgId, payload);
      setOrganisationRecord(updated || payload);
      setOrgLogo(updated?.orgLogo || logoDraft);
      setIsLogoModalOpen(false);
    } catch (error) {
      console.error('Error updating organisation logo', error);
      setLogoError('Unable to save logo. Please try again.');
    } finally {
      setIsSavingLogo(false);
    }
  };

  useEffect(() => {
    if (sessionStorage.getItem(HOME_SKIP_AUTO_TOUR_ONCE_KEY) === 'true') {
      sessionStorage.removeItem(HOME_SKIP_AUTO_TOUR_ONCE_KEY);
    }
  }, []);

  useEffect(() => {
    const runTriggeredTour = () => {
      if (!canUseTourGuide || !tourGuideReady) {
        return;
      }

      const triggerValue = sessionStorage.getItem(PRODUCT_TOUR_TRIGGER_ONCE_KEY) || '';
      if (!triggerValue || triggerValue === lastManualTourTriggerRef.current) {
        return;
      }

      lastManualTourTriggerRef.current = triggerValue;
      sessionStorage.removeItem(PRODUCT_TOUR_TRIGGER_ONCE_KEY);
      localStorage.removeItem(PRODUCT_TOUR_TRIGGER_KEY);
      autoTourStartedRef.current = true;
      if (autoTourTimerRef.current) {
        window.clearTimeout(autoTourTimerRef.current);
        autoTourTimerRef.current = null;
      }
      startTour().catch((err) => {
        console.error('Failed to start triggered home tour', err);
      });
    };

    runTriggeredTour();
    window.addEventListener('product-tour-triggered', runTriggeredTour);
    return () => {
      window.removeEventListener('product-tour-triggered', runTriggeredTour);
    };
  }, [canUseTourGuide, startTour, tourGuideReady]);

  return (
    <div className="p-24">
      <div
        className="card w-100 h-100"
        style={{ minHeight: "calc(100vh - 120px)" }}
      >
        <div className="card-header d-flex justify-content-start align-items-center">
          <div
            className="position-relative"
            style={{ height: "70px", width: "auto", cursor: 'pointer' }}
            onClick={openLogoModal}
            onMouseEnter={() => setIsLogoHovered(true)}
            onMouseLeave={() => setIsLogoHovered(false)}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openLogoModal();
              }
            }}
            title="Replace organisation logo"
          >
            <img
              src={logoSrc}
              alt="Organisation Logo"
              style={{ height: "70px", width: "auto" }}
            />
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center text-white"
              style={{
                background: 'rgba(0,0,0,0.45)',
                opacity: isLogoHovered ? 1 : 0,
                transition: 'opacity 0.2s ease',
                pointerEvents: 'none',
                fontSize: '0.82rem',
                fontWeight: 600,
              }}
            >
              <Icon icon="mdi:camera-outline" className="me-1" />
            </div>
          </div>
          <div className='px-32 pt-16'>
            <p className='display-6 fw-normal mb-6' style={{ fontSize: '32px' }}>Hello, {userFirstName}</p>
            <p>What do you want to do today?</p>
          </div>
        </div>
        <div className="card-body">

          <div className="row g-3">
            <div className='col-md-12 col-lg-6' style={{ maxHeight: 'calc(100vh - 310px)' }}>
              <div className="row">
                {isSuperAdmin && (
                  <div
                    className="col-md-6 col-xl-6 col-xxl-4 my-8"
                  >
                    <div
                      className="card h-100 text-center p-16 d-flex flex-column align-items-center action-card-border"
                    >
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle mb-8"
                        style={{ backgroundColor: "#eee", width: "32px", height: "32px" }}
                      >
                        <Icon icon="mdi:calculator" style={{ fontSize: "16px" }} />
                      </div>
                      <p className="mb-0 fw-semibold">Recalc</p>
                      <p className="text-muted small mb-20">Recalculate Work Orders</p>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleRecalculation}
                        disabled={triggering}
                      >
                        {triggering ? 'Running recalculation...' : 'Recalculate Work Orders'}
                      </button>
                      {triggerMessage && (
                        <p className="text-success mt-8 mb-0">{triggerMessage}</p>
                      )}
                      {triggerError && (
                        <p className="text-danger mt-8 mb-0">{triggerError}</p>
                      )}
                    </div>
                  </div>)}
                {cards.map(({ title, icon, description, link, action, visible }) =>
                  visible ? ( // Only show the card if user has access
                    <div
                      key={title}
                      className="col-md-6 col-xl-6 col-xxl-4 my-8"
                    >
                      <div
                        className="card h-100 text-center p-16 d-flex flex-column align-items-center action-card-border"
                      >
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle mb-8"
                          style={{ backgroundColor: "#eee", width: "32px", height: "32px" }}
                        >
                          <Icon icon={icon} style={{ fontSize: "16px" }} />
                        </div>
                        <p className="mb-0 fw-semibold">{title}</p>
                        <p className="text-muted small mb-20">{description}</p>
                        <Link
                          to={link}
                          className="text-primary w-75 mb-12 text-sm"
                          data-tg-tour={
                            title === 'Manage Events'
                              ? 'Events enable you to group related work and manage asset downtime.'
                              : title === 'Manage Work Orders'
                                ? 'Work orders record and contain all the necessary information to executive a discrete job, such as repairing a defect or replacing a component.'
                                : `Open ${title.toLowerCase()} to work on ${description.toLowerCase()}.`
                          }
                          data-tg-group="home-tour"
                          data-tg-order={HOME_ACTION_START_ORDER + 3 + cards.findIndex((card) => card.title === title)}
                          data-tg-title={title}
                        >
                          {action} {">"}
                        </Link>
                      </div>
                    </div>) : null
                )}
              </div>
            </div>
            <div className='col-md-12 col-lg-6' style={{ maxHeight: 'calc(100vh - 310px)' }}>
              {/* <DailySummary /> */}

              <div className="col-md-12">
                <div
                  className="card h-100 p-16 action-card-border"
                >
                  <div className='row'>
                    <div className='col-md-12 pb-8'>
                      <p className='mb-3 fw-semibold mb-24'>Get the most out of Samurai</p>
                      <div className='row g-2'>
                        <div className='col-12 col-md-4 mb-12'>
                          <a
                            href="http://help.samurai-cmms.com.au/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-100 text-decoration-none"
                          >
                            <button className="btn btn-outline-primary w-100">
                              <Icon
                                icon="mdi:book-open-page-variant-outline"
                                className="text-xl me-4"
                                style={{ position: 'relative', top: '-2px' }}
                              />
                              Help Docs
                            </button>
                          </a>
                        </div>
                        <div className='col-12 col-md-4 mb-12'>
                          <button
                            className='btn btn-outline-primary w-100 d-inline-flex align-items-center justify-content-center gap-2'
                            onClick={handleStartTour}
                            disabled={!canUseTourGuide || !tourGuideReady}
                            title={!canUseTourGuide ? 'Enable the tour guide in Settings' : 'Launch the home tour'}
                          >
                            <Icon
                              icon='mdi:compass-outline'
                              className='text-xl'
                            />
                            <span>Product Tour</span>
                          </button>
                        </div>
                        <div className='col-12 col-md-4 mb-12'>
                          <button className='btn btn-outline-primary w-100'>
                            <Icon
                              icon='mdi:lifebuoy'
                              className='text-xl me-2'
                              style={{ position: 'relative', top: '-2px' }}
                            />
                            Support
                          </button>
                        </div>
                        {canSeeInviteCard && (
                          <div className='col-12 col-md-4'>
                            <div
                              className="w-100 border border-primary-600 rounded py-12 px-12 d-flex flex-column align-items-center text-center"
                              style={{ height: "230px" }}
                            >
                              <div
                                className="d-flex align-items-center justify-content-center rounded-circle mb-16 bg-primary-focus text-primary"
                                style={{ width: "48px", height: "48px" }}
                              >
                                <Icon icon='mdi:user-outline' style={{ fontSize: "28px" }} />
                              </div>
                              <div className="mb-2 fw-semibold text-primary">
                                Invite Users
                              </div>
                              <span className="text-sm text-primary pb-16">Invite your team to start collaborating in Samurai.</span>
                              <button
                                className='btn btn-outline-primary'
                                onClick={() => canInvite && setShowInviteModal(true)}
                                disabled={!canInvite}
                                data-tg-tour="Invite teammates so they can log in and collaborate in the workspace."
                                data-tg-group="home-tour"
                                data-tg-order={HOME_ACTION_START_ORDER + 1}
                                data-tg-title="Invite users"
                              >
                                <Icon
                                  icon='mdi:user-outline'
                                  className='text-xl me-2'
                                  style={{ position: 'relative', top: '-2px' }}
                                />
                                Invite Users
                              </button>
                            </div>
                          </div>
                        )}
                        <div className='col-12 col-md-4'>
                          <div
                            className="w-100 border border-primary-600 rounded p-8 px-12 d-flex flex-column align-items-center text-center"
                            style={{ height: "230px" }}
                          >
                            <div className="fw-semibold text-primary mb-6">
                              Get the Android App
                            </div>
                            <div className="mb-12">
                              {/* Shown on small screens (sm and below) */}
                              <img
                                src={qrPlayImageUrl}
                                alt="QR Code"
                                width={102}
                                height={102}
                                className="d-block d-xxl-none"
                              />

                              {/* Shown on medium screens and up */}
                              <img
                                src={qrPlayImageUrl}
                                alt="QR Code"
                                width={120}
                                height={120}
                                className="d-none d-xxl-block"
                              />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <img
                                src="assets/images/google-play.png"
                                width="130"
                                alt="Google Play"
                              />
                            </div>
                          </div>
                        </div>
                        <div className='col-12 col-md-4'>
                          <div
                            className="w-100 border border-primary-600 rounded p-8 px-12 d-flex flex-column align-items-center text-center"
                            style={{ height: "230px" }}
                          >
                            <div className="fw-semibold text-primary mb-6">
                              Get the iOS App
                            </div>
                            <div className="mb-12">
                              {/* Shown on small screens (sm and below) */}
                              <img
                                src={qrIosImageUrl}
                                alt="QR Code"
                                width={102}
                                height={102}
                                className="d-block d-xxl-none"
                              />

                              {/* Shown on medium screens and up */}
                              <img
                                src={qrIosImageUrl}
                                alt="QR Code"
                                width={120}
                                height={120}
                                className="d-none d-xxl-block"
                              />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <img
                                src="assets/images/apple-store.png"
                                width="130"
                                alt="Apple Store"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </div>
      {isLogoModalOpen && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Replace Organisation Logo</h5>
                <button type="button" className="btn-close" onClick={() => setIsLogoModalOpen(false)} />
              </div>
              <div className="modal-body">
                <div className="d-flex flex-column align-items-center gap-12">
                  <img
                    src={getLogoSrc(logoDraft) || logoSrc}
                    alt="Logo Preview"
                    style={{ maxHeight: '140px', width: 'auto', objectFit: 'contain' }}
                  />
                  <input
                    type="file"
                    accept=".png,.jpg,.jpeg"
                    className="form-control"
                    onChange={handleLogoFileChange}
                  />
                  {logoError && <div className="text-danger small">{logoError}</div>}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setIsLogoModalOpen(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSaveLogo} disabled={isSavingLogo}>
                  {isSavingLogo ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showInviteModal && canInvite && canSeeInviteCard && (
        <InviteUsersModal
          tenantId={tenantId}
          onClose={() => setShowInviteModal(false)}
        />
      )}
    </div>
  );
};

export default HomeLayer;
